import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsService } from '../services/settings.service';
import type { SettingValue, LanguageSyncModel } from '../services/settings.service';

/**
 * Query keys for settings
 */
export const settingsKeys = {
  all: ['settings'] as const,
  theme: () => [...settingsKeys.all, 'theme'] as const,
  byNames: (names: string) => [...settingsKeys.all, 'byNames', names] as const,
  languages: () => [...settingsKeys.all, 'languages'] as const,
};

/**
 * Hook to get theme override setting
 * @param enabled Whether to fetch the theme (default: true)
 */
export const useThemeOverride = (enabled = true) => {
  return useQuery({
    queryKey: settingsKeys.theme(),
    queryFn: () => settingsService.getThemeOverride(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    enabled,
    retry: false, // Don't retry on 401 errors
  });
};

/**
 * Hook to save theme override setting
 */
export const useSaveThemeOverride = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (themeJson: string) => settingsService.setThemeOverride(themeJson),
    onSuccess: () => {
      // Invalidate theme query to refetch
      queryClient.invalidateQueries({ queryKey: settingsKeys.theme() });
    },
  });
};

/**
 * Hook to get settings by names
 */
export const useSettings = (names: string, enabled = true) => {
  return useQuery({
    queryKey: settingsKeys.byNames(names),
    queryFn: () => settingsService.getSettings(names),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to get user-level settings by names
 */
export const useUserSettings = (names: string, enabled = true) => {
  return useQuery({
    queryKey: [...settingsKeys.all, 'user', names] as const,
    queryFn: () => settingsService.getUserSettings(names),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to save settings
 */
export const useSaveSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: SettingValue[]) => settingsService.setSettings(settings),
    onSuccess: () => {
      // Invalidate all settings queries
      queryClient.invalidateQueries({ queryKey: settingsKeys.all });
    },
  });
};

/**
 * Hook to get available languages
 */
export const useLanguages = (enabled = true) => {
  return useQuery({
    queryKey: settingsKeys.languages(),
    queryFn: () => settingsService.getLanguages(),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes - languages don't change often
  });
};

/**
 * Hook to sync language texts
 */
export const useSyncLanguageText = () => {
  return useMutation({
    mutationFn: (model: LanguageSyncModel) => settingsService.syncLanguageText(model),
  });
};

/**
 * Hook to reset role permissions
 */
export const useResetRolePermissions = () => {
  return useMutation({
    mutationFn: (role: string) => settingsService.resetRolePermissions(role),
  });
};

/**
 * Hook to clear application cache
 */
export const useClearCache = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsService.clearCache(),
    onSuccess: () => {
      // Invalidate all queries to force refetch
      queryClient.invalidateQueries();
    },
  });
};
